<template>
    <div class="content">
        <div>
            <team-member-invoices-overview-toolbar
                :class="$root.isTablet ? 'tablet-filter-container' : ''"
                @onFilterInvoices="handleFilterOrSearch"
                @onExportClicked="handleExportClick"
                @onSendToAccountingPackageClicked="processInvoicesBasedOnFilter"
                @onCreateInvoice="handleGenerateInvoice"
                :team-members="() => this.teamMembers"
                :team-region-id="filters.teamRegionId"
                :batch-filter-list="batchFilterList"
                :disable-export="shouldDisableExport"
            />
        </div>
        <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
            <md-card class="custom-card">
                <tabs
                    :tab-name="tabNames"
                    color-button="success"
                    class="custom-tab-list"
                    @switch-panel="switchTab"
                    :selected-panel="replaceDashToSpacesAndCapitalizeFirstLetter(activeTab)"
                >
                    <template v-for="(tabName, index) in tabNames" :slot="`tab-pane-${index + 1}`">
                        <div class="pull-right invoice-contents" :key="tabName">
                            <component
                                class="invoice-contents"
                                :is="tabComponents[index]"
                                :filters="filters"
                                @batchFilterUpdated="handleBatchFilterUpdated"
                                @selectionChanged="handleSelectionChanged"
                                @listChanged="handleListChanged"
                                ref="tabComponent"
                            />
                        </div>
                    </template>
                </tabs>
            </md-card>
        </div>
        <div class="progress-container" v-if="showProgressBox">
            <div class="button-close">
                <md-button class="md-default md-just-icon md-round" @click="handleCloseProgressBox">
                    <md-icon>close</md-icon>
                </md-button>
            </div>
            <div class="progress-text-section" v-if="!isProgressComplete">
                <div>
                    <div class="btn-loader route-loader"></div>
                </div>
                <div v-if="totalInvoice == 0">Preparing invoices...</div>
                <div v-else>{{ currentProcessedInvoice }} of {{ totalInvoice }} invoices have been generated.</div>
            </div>
            <div v-else class="progress-text-section">
                <div>
                    <md-icon class="green-check">check_circle</md-icon>
                </div>
                <div>
                    Process has been completed.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { PAGINATION_DEFAULTS } from '@/utils/defaults';
import { FEATURE_NAMES, DATE_TYPES, HUB_NAMES, HUB_ACTIONS, SIGNALR_CONNECTION_STATE } from '@/utils/constants';
import { handleRequests, showErrorMessage, downloadFile } from '@/helpers';
import { Tabs } from '@/components';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { FeatureManager } from '@/directives';
import TeamMemberInvoicesOverview from './TeamMemberInvoicesOverview';
import TeamMemberInvoicesDraft from './TeamMemberInvoicesDraft';
import { TeamMemberInvoicesOverviewToolbar } from './components';

export default {
    name: 'TeamMemberInvoices',
    mixins: [GeneralMixin, FeatureManager],
    components: {
        Tabs,
        TeamMemberInvoicesOverview,
        TeamMemberInvoicesOverviewToolbar,
        TeamMemberInvoicesDraft
    },
    data() {
        return {
            filters: {
                teamMemberPublicUserId: '',
                teamRegionId: null
            },
            pagination: PAGINATION_DEFAULTS,
            batchFilterList: [],
            selectedMessage: 0,
            selectedTeamMemberInvoiceIds: [],
            invoicesList: [],
            showProgressBox: false,
            isProgressComplete: false,
            currentProcessedInvoice: 0,
            totalInvoice: 0,
            tabNames: [],
            tabRequiredFeature: [
                { name: 'Release', featureId: null },
                { name: 'Drafts', featureId: FEATURE_NAMES.DraftInvoices }
            ],
            activeTab: this.$route.params.type || 'release',
            tabComponents: ['TeamMemberInvoicesOverview', 'TeamMemberInvoicesDraft'],
            teamMembers: [],
            generatedInvoiceIds: [],
            signalRHandlers: [],
            signalRTimeout: null,
            reactivityTrigger: 0 // Add this to force reactivity
        };
    },
    async mounted() {
        await this.getTeamMembers();
        this.activeTabs();

        // Set up SignalR handlers
        this.signalRHandlers = [
            {
                action: 'TeamMemberInvoiceBatchCompleted',
                handler: async (batchId, status, completedInvoiceCount) => {
                    this.handleTeamMemberInvoiceBatchCompleted(batchId, status, completedInvoiceCount);
                }
            },
            {
                action: 'TeamMemberInvoiceBatchFailed',
                handler: async (status, completedInvoiceCount, failedInvoiceCount) => {
                    this.handleTeamMemberInvoiceBatchFailed(status, completedInvoiceCount, failedInvoiceCount);
                }
            },
            {
                action: 'InvoiceSentResult',
                handler: this.handleInvoiceSentResult
            },
            {
                action: 'BulkInvoiceResult',
                handler: this.handleBulkInvoiceResult
            },
            {
                action: 'TotalInvoiceToBeGenerated',
                handler: this.handleTotalInvoiceToBeGenerated
            },
            {
                action: 'InvoiceGenerated',
                handler: this.handleInvoiceGenerated
            },
            {
                action: 'DriverHasNoLineTypes',
                handler: async (driverName) => {
                    await this.handleDriverNoLineTypes(driverName);
                }
            },
            {
                action: 'TeamMemberInvoiceDraftSent',
                handler: this.updateDraftInvoiceHasBeenSent
            }
        ];

        this.setupSignalR();
    },
    beforeDestroy() {
        clearTimeout(this.signalRTimeout);
        this.removeSignalRHandlers();
    },
    computed: {
        ...mapGetters({
            getConnectionStatus: 'signalR/getConnectionStatus',
            getConnection: 'signalR/getConnection'
        }),
        shouldDisableExport() {
            // Add reactivityTrigger to force re-computation
            // eslint-disable-next-line no-unused-vars
            const trigger = this.reactivityTrigger;
            const tabComponent = this.$refs.tabComponent && this.$refs.tabComponent[0];
            if (!tabComponent) 
                return true;

            const hasSelection = tabComponent.selectedMessage && tabComponent.selectedMessage > 0;
            const hasInvoices = tabComponent.invoicesList && tabComponent.invoicesList.length > 0;

            // Disable export if there's no selection OR no invoices available
            return !hasSelection && !hasInvoices;
        }
    },
    methods: {
        async getTeamMembers(tripDate = null) {
            this.teamMembers = await this.$store.dispatch('team/FETCH_TEAM_MEMBERS', {
                date: tripDate,
                teamRegionId: this.selectedTeamRegionId
            });
        },
        replaceDashToSpacesAndCapitalizeFirstLetter(str) {
            return str
                .toLowerCase()
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        async switchTab(panel) {
            const tab = panel
                .toLowerCase()
                .split(' ')
                .join('-');

            this.activeTab = tab;
            this.$router.replace({ path: `/team-member-invoices/list/${tab}`, query: {} });
        },
        handleSelectionChanged(args) {
            // Increment to trigger computed property recalculation
            this.reactivityTrigger = this.reactivityTrigger + 1;
        },
        handleListChanged(args) {
            // Increment to trigger computed property recalculation
            this.reactivityTrigger = this.reactivityTrigger + 1;
        },
        async handleFilterOrSearch(data) {
            this.filters = Object.assign(this.filters, data);
            this.$refs.tabComponent[0].getInvoicesList();
        },
        async handleExportClick(data) {
            this.filters = Object.assign(this.filters, data);
            this.$_handleLoaderState(true, 'EXPORTING...');
            try {
                const isBulk =
                    this.$refs.tabComponent[0].selectedMessage && this.$refs.tabComponent[0].selectedMessage > 0;
                const endpoint = `/api/team-member-invoices/${isBulk ? 'bulk/export' : 'export'}`;
                const payload = {
                    method: isBulk ? 'post' : 'get',
                    responseType: 'blob',
                    ...(isBulk
                        ? { data: { invoiceIds: this.$refs.tabComponent[0].selectedTeamMemberInvoiceIds } }
                        : { params: { ...this.cleanFilters() } })
                };

                const response = await handleRequests(endpoint, payload);
                downloadFile(response.data, 'invoicesList.xlsx');
            } catch (error) {
                const message = 'Error in exporting invoices.';
                showErrorMessage(this, message, error);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        async processInvoicesBasedOnFilter() {
            this.isProcessAll = true;
            this.$notify({
                message: 'Invoices will be processed in the background.',
                type: 'warning',
                duration: 6000
            });
            try {
                const payload = {
                    method: 'post',
                    params: {
                        ...this.cleanFilters()
                    }
                };
                const endpoint = `/api/team-member-invoices/send-to-accounting/all`;
                await handleRequests(endpoint, payload);
                this.isProcessAll = false;
            } catch (error) {
                this.isProcessAll = false;
                const message = 'Error in sending the invoices to the accounting package';
                showErrorMessage(this, message, error);
            }
        },
        handleGenerateInvoice() {
            this.isProgressComplete = false;
            this.showProgressBox = true;
            this.currentProcessedInvoice = 0;
            this.totalInvoice = 0;
        },
        handleBatchFilterUpdated(batchFilter) {
            this.batchFilterList = batchFilter;
        },
        activeTabs() {
            const activeTabs = [];
            this.tabRequiredFeature.forEach((tab) => {
                if (tab.featureId === null) {
                    activeTabs.push(tab.name);
                } else if (this.$_hasFeature(tab.featureId)) {
                    activeTabs.push(tab.name);
                }
            });

            this.tabNames = activeTabs;
        },
        async setupSignalR() {
            try {
                await this.$store.dispatch('signalR/connect', HUB_NAMES.InvoicingHub);
                const connection = this.getConnection(HUB_NAMES.InvoicingHub);

                // Verify connection is established before proceeding
                if (!connection || connection.state !== SIGNALR_CONNECTION_STATE.Connected) {
                    throw new Error('SignalR connection not established');
                }

                await connection.invoke(HUB_ACTIONS.JoinToTeamChannel);
                await this.$store.dispatch('signalR/registerHandlers', {
                    hubName: HUB_NAMES.InvoicingHub,
                    handlers: this.signalRHandlers
                });
            } catch (e) {
                console.error('Error connecting to invoicing hub:', e);
            }
        },
        removeSignalRHandlers() {
            try {
                const connection = this.getConnection(HUB_NAMES.InvoicingHub);

                if (connection && connection.state === SIGNALR_CONNECTION_STATE.Connected) {
                    this.$store.dispatch('signalR/unregisterHandlers', {
                        hubName: HUB_NAMES.InvoicingHub,
                        handlers: this.signalRHandlers
                    });
                    connection.invoke(HUB_ACTIONS.RemoveFromTeamChannel);
                }
            } catch (error) {
                console.error('Error removing SignalR handlers:', error);
            }
        },
        handleTeamMemberInvoiceBatchCompleted(batchId, status, completedInvoiceCount) {
            this.isProgressComplete = true;

            if (status === 'CompletedNoRecords') {
                this.handleCloseProgressBox();
                this.$notifySuccess('Invoice batch completed but no uninvoiced stops found in range.', 60000);
            } else {
                this.handleCloseProgressBox();

                let successMessage = `${completedInvoiceCount} Invoices have been generated.`;
                if (completedInvoiceCount === 1) {
                    successMessage = `${completedInvoiceCount} Invoice has been generated.`;
                }
                this.$notifySuccess(successMessage, 60000);
            }
        },
        handleTeamMemberInvoiceBatchFailed(status, completedInvoiceCount, failedInvoiceCount) {
            this.isProgressComplete = true;
            this.handleCloseProgressBox();

            this.$notify({
                message: `Completed Invoices: ${completedInvoiceCount} Failed Invoices: ${failedInvoiceCount}.<br /><br /> Please try again. If error persists please contact support.`,
                type: 'warning',
                duration: 10000
            });
        },
        handleCloseProgressBox() {
            this.showProgressBox = false;
        },
        handleInvoiceSentResult(invoiceId, reference, note) {
            const invoice = this.$refs.tabComponent[0].invoicesList.find(
                (x) => x.teamMemberInvoiceId === Number(invoiceId)
            );

            if (invoice == null) 
                return;

            if (reference != null) {
                invoice.accountingSystemRef = reference;
            } else {
                invoice.accountingSystemNote = note;
            }

            if (invoice.isSyncing === undefined) {
                this.$set(invoice, 'isSyncing', false);
            } else {
                invoice.isSyncing = false;
            }
        },
        async handleBulkInvoiceResult(result, invoices) {
            this.$_handleLoaderState(false);

            let successMessage = 'Successfully processed the invoices.';
            let errorMessage = 'Error in sending the invoices to the accounting package.';

            if (invoices?.length) {
                if (invoices.length === 1) {
                    successMessage = 'Successfully processed the invoice.';
                    errorMessage = 'Error in sending the invoice to the accounting package.';
                }

                for (let i = 0; i < invoices.length; i++) {
                    const currentInvoice = invoices[i];
                    const matchingInvoice = this.$refs.tabComponent[0].invoicesList.find(
                        (x) => x.teamMemberInvoiceId === Number(currentInvoice.InvoiceId)
                    );

                    if (matchingInvoice) {
                        if (matchingInvoice.isSyncing === undefined) {
                            this.$set(matchingInvoice, 'isSyncing', false);
                        } else {
                            matchingInvoice.isSyncing = false;
                        }

                        matchingInvoice.accountingSystemRef = currentInvoice.Reference;
                        matchingInvoice.accountingSystemNote = currentInvoice.Note;
                    }
                }
            }

            if (result === 'Success') {
                this.$notifySuccess(successMessage, 10000);
            } else {
                this.$notifyError(errorMessage);

                if (this.isProcessAll) {
                    this.selectedTeamMemberInvoiceIds.forEach((id) => {
                        const invoice = this.$refs.tabComponent[0].invoicesList.find(
                            (x) => x.teamMemberInvoiceId === Number(id)
                        );

                        if (invoice.isSyncing === undefined) {
                            this.$set(invoice, 'isSyncing', false);
                        } else {
                            invoice.isSyncing = false;
                        }
                    });
                }
            }
        },
        handleTotalInvoiceToBeGenerated(total) {
            this.totalInvoice = Number(total);
        },
        async handleInvoiceGenerated(teamMemberInvoiceId) {
            if (!teamMemberInvoiceId) {
                return;
            }

            this.generatedInvoiceIds.push(teamMemberInvoiceId);

            this.currentProcessedInvoice = this.currentProcessedInvoice + 1;

            // Debounced method: will only be actually executed once all the generated invoice ids have been sent from the server
            await this.tryPushGeneratedInvoices();
        },
        // eslint-disable-next-line func-names
        tryPushGeneratedInvoices: _.debounce(async function() {
            // If the number of the existing invoices plus the number of the generated invoices exceed the page capacity,
            // reload the page instead of just appending to the list
            const getSingleInvoicePromises = [];
            let shouldPushToInvoiceList = true;

            for (let i = 0; i < this.generatedInvoiceIds.length; i++) {
                if (i + 1 + this.$refs.tabComponent[0].invoicesList.length > this.pagination.perPage) {
                    shouldPushToInvoiceList = false;
                    break;
                }

                getSingleInvoicePromises.push(this.getSingleInvoice(Number(this.generatedInvoiceIds[i])));
            }

            this.generatedInvoiceIds = [];
            if (shouldPushToInvoiceList) {
                const invoices = await Promise.all(getSingleInvoicePromises);

                for (let i = 0; i < invoices.length; i++) {
                    const invoice = invoices[i];

                    if (invoice.status === 'Draft' && this.$refs.tabComponent[0].isDraft) {
                        if (invoice && invoice.invoiceNumber) {
                            this.$refs.tabComponent[0].invoicesList.push(invoice);
                        }
                    } else if (invoice.status !== 'Draft' && !this.$refs.tabComponent[0].isDraft) {
                        if (invoice && invoice.invoiceNumber) {
                            this.$refs.tabComponent[0].invoicesList.push(invoice);
                        }
                    }
                }
            } else {
                this.handleChangePage(this.pagination.currentPage, this.pagination.perPage);
            }
        }, 1000),
        async handleDriverNoLineTypes(driverName) {
            this.$notify({
                message: `Driver: <b>'${driverName}'</b>, has no invoice line types setup, please setup in driver card.`,
                type: 'warning',
                duration: 10000
            });
        },
        async getSingleInvoice(teamMemberInvoiceId) {
            const endpoint = `/api/team-member-invoices/${teamMemberInvoiceId}`;
            const { data } = await handleRequests(endpoint);
            return data;
        },
        cleanFilters() {
            Object.keys(this.filters).forEach((e) => {
                if (!this.filters[e]) 
                    delete this.filters[e];
            });

            return this.filters;
        },
        updateDraftInvoiceHasBeenSent(invoiceId) {
            const invoice = this.$refs.tabComponent[0].invoicesList?.find(
                (x) => x.teamMemberInvoiceId === Number(invoiceId)
            );

            if (invoice == null) 
                return;

            invoice.draftEmailHasBeenSent = true;
            invoice.lastDraftEmailDateSent = moment().format(DATE_TYPES.standardDateTime);
            invoice.newDraftCostAdded = false;
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .filter-steps--container.tablet-filter-container {
    width: initial;
    float: left;
    margin-left: 20px;
}

.custom-tab-list {
    padding: 10px;
    position: initial;
}

.invoice-contents {
    display: contents !important;
}

.btn-loader {
    border: 4px solid #eeeeee;
    border-radius: 50%;
    border-top: 4px solid #2b93ff;
    width: 24px;
    height: 24px;
    -webkit-animation: spin 1s linear infinite;
    animation: spin 1s linear infinite;
    top: 85%;
    left: 48%;
}

@-webkit-keyframes spin {
    0% {
        -webkit-transform: rotate(0deg);
    }

    100% {
        -webkit-transform: rotate(360deg);
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.hide-progressbox {
    display: none;
}
.green-check {
    color: #4caf50 !important;
}

.progress-container {
    min-width: 200px;
    max-width: 250px;
    background-color: #fff;
    position: fixed;
    right: 12px;
    bottom: 8px;
    z-index: 99;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
    padding: 0 10px 10px 10px;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;

    .progress-text-section {
        display: table;
        padding-top: 12px;
        > div {
            display: table-cell;
            vertical-align: middle;
        }

        > div:last-child {
            padding-left: 10px;
        }
    }

    ::v-deep .md-button {
        height: 20px;
        min-width: 20px;
        width: 20px;
        position: absolute;
        top: -12px;
        right: -12px;

        i {
            font-size: 15px !important;
        }
    }
}

.btn-loader {
    border: 4px solid #eeeeee;
    border-radius: 50%;
    border-top: 4px solid #2b93ff;
    width: 24px;
    height: 24px;
    -webkit-animation: spin 1s linear infinite;
    animation: spin 1s linear infinite;
    top: 85%;
    left: 48%;
}

.route-loader {
    top: 20% !important;
    left: 0 !important;
}
</style>
